import posthog from 'posthog-js'
import type { AnalyticsClient } from '../types'
import { sanitizeUrl, toScrubbedError, redactText } from '../sanitize'

let initialized = false

/**
 * Init posthog-js ONCE. This is called ONLY after consent is granted — either from the
 * banner's Accept (grantConsent) or on mount for a returning consented user. Because init
 * is deferred until consent, we use a normal capturing config: no opt_out_capturing_by_default
 * and (critically) no advanced_disable_decide.
 *
 * Why not the opt-out-by-default + advanced_disable_decide approach: advanced_disable_decide
 * latches the /flags (decide) flow off at init, and re-enabling it via set_config does NOT
 * restore event delivery — events queue and never send. Deferring init is the clean pattern:
 * ZERO network before consent (the SDK isn't running) and normal delivery after.
 */
export function initPosthog(key: string): void {
  if (initialized || typeof window === 'undefined') return
  initialized = true
  posthog.init(key, {
    api_host: '/ingest', // reverse-proxied to eu.i.posthog.com (see next.config.js rewrites)
    ui_host: 'https://eu.posthog.com',
    person_profiles: 'identified_only',
    capture_pageview: false, // App Router: captured manually on route change
    capture_pageleave: true,
    disable_session_recording: true,
    respect_dnt: true,
    mask_all_text: true,
    mask_all_element_attributes: true,
    capture_exceptions: true, // safe: init (and thus the autocapture script) only runs post-consent
    before_send: (event) => {
      if (!event) return event
      const p: Record<string, unknown> = event.properties || {}
      if (typeof p.$current_url === 'string') p.$current_url = sanitizeUrl(p.$current_url)
      if (typeof p.$referrer === 'string' && p.$referrer !== '$direct') p.$referrer = sanitizeUrl(p.$referrer)
      if (typeof p.$pathname === 'string') p.$pathname = sanitizeUrl(p.$pathname)

      // capture_exceptions autocaptures $exception events that bypass captureException's
      // toScrubbedError() scrubbing — redact the raw exception text here instead.
      if (typeof p.$exception_message === 'string') p.$exception_message = redactText(p.$exception_message)
      if (typeof p.$exception_stack_trace_raw === 'string') {
        p.$exception_stack_trace_raw = redactText(p.$exception_stack_trace_raw)
      }
      if (Array.isArray(p.$exception_list)) {
        for (const entry of p.$exception_list as Array<Record<string, unknown>>) {
          if (entry && typeof entry.value === 'string') entry.value = redactText(entry.value)
        }
      }

      // PostHog attaches the landing URL under $set_once/$set as $initial_current_url etc,
      // which the top-level $current_url/$referrer/$pathname sanitization above never touches.
      for (const bag of [p.$set_once, p.$set]) {
        if (!bag || typeof bag !== 'object') continue
        for (const [k, v] of Object.entries(bag as Record<string, unknown>)) {
          if (/url|referrer|pathname/i.test(k) && typeof v === 'string') {
            ;(bag as Record<string, unknown>)[k] = sanitizeUrl(v)
          }
        }
      }

      return event
    },
  })
}

export const posthogClientAdapter: AnalyticsClient = {
  identify(distinctId) { posthog.identify(distinctId) },
  capture(event, props) { posthog.capture(event, props) },
  pageview(url) { posthog.capture('$pageview', { $current_url: sanitizeUrl(url) }) },
  isFeatureEnabled(key) { return posthog.isFeatureEnabled(key) },
  captureException(error, props) { posthog.captureException(toScrubbedError(error), props) },
  reset() { posthog.reset() },
}
